'use client';

import * as React from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Avatar,
  BlurFade,
  Button,
  cn,
  FocusableLink,
} from 'ui';
import Avatar1 from '../../../../public/images/avatar-1.jpg';
import Avatar3 from '../../../../public/images/avatar-3.jpg';
import Qing from '../../../../public/images/root/qing.jpeg';

export function Faq(): JSX.Element {
  const router = useRouter();
  return (
    <div className="px-4">
      <section
        className="mx-auto flex max-w-7xl flex-col items-center pt-16 text-center xs:pt-24"
        id="faq"
      >
        <BlurFade delay={0.1}>
          <h2 className="text-display-md font-semibold text-gray-900">
            Frequently Asked Questions
          </h2>
        </BlurFade>
        <BlurFade delay={0.2}>
          <p className="pt-5 text-xl text-gray-600">
            Everything you need to know about the product and billing.
          </p>
        </BlurFade>
        <div className="py-16">
          <Accordion className="px-4 sm:w-[470px] lg:w-[768px]" type="multiple">
            <BlurFade delay={0.15}>
              <AccordionItem value="privacy">
                <FaqAccordionTrigger>
                  Is Quotion privacy-friendly?
                </FaqAccordionTrigger>
                <FaqAccordionContent className="space-y-2">
                  <p>
                    Yes, Quotion can only access the folders and notes you have
                    shared with us; we can never access anything you haven't
                    shared. Check out{' '}
                    <FocusableLink
                      target="_blank"
                      className="underline"
                      href="https://support.apple.com/en-ie/guide/icloud-ipad/mmbc2b562111/icloud#:~:text=Only%20the%20people%20you%20invite%20can%20view%20and%20edit%20the%20folder."
                    >
                      Apple's official documentation
                    </FocusableLink>
                    .
                  </p>
                  <p>
                    For analytics, we've built our own privacy-focused,
                    cookie-free analytics system that keeps your sites data
                    secure and never shares it with 3rd parties.
                  </p>
                </FaqAccordionContent>
              </AccordionItem>
            </BlurFade>
            <BlurFade delay={0.2}>
              <AccordionItem value="plan-expensive">
                <FaqAccordionTrigger>
                  Why are the plans expensive?
                </FaqAccordionTrigger>
                <FaqAccordionContent className="space-y-4">
                  <p>
                    Quotion offers a premium website experience for Apple Notes
                    users, featuring high{' '}
                    <FocusableLink href="#performance" className="underline">
                      performance
                    </FocusableLink>
                    , robust{' '}
                    <FocusableLink
                      href="https://docs.quotion.co/features/password-protection"
                      target="_blank"
                      className="underline"
                    >
                      security
                    </FocusableLink>
                    {', '}
                    <FocusableLink
                      href="https://docs.quotion.co/features/web-analytics"
                      target="_blank"
                      className="underline"
                    >
                      built-in analytics
                    </FocusableLink>
                    {', '}
                    <FocusableLink
                      href="https://docs.quotion.co/features/newsletter"
                      target="_blank"
                      className="underline"
                    >
                      unlimited newsletters
                    </FocusableLink>
                    , and{' '}
                    <FocusableLink
                      href="https://docs.quotion.co/features/ai-generated-image-caption"
                      target="_blank"
                      className="underline"
                    >
                      AI-generated image captions
                    </FocusableLink>
                    . These advanced features are supported by high-performing,
                    reliable services, which contribute to the overall cost.
                  </p>
                  <p>
                    We also provide several ways to make our plans more
                    affordable:
                  </p>
                  <ul className="ml-4 list-inside list-disc">
                    <li>
                      <strong>Annual Plan:</strong> Includes 2 months free.
                    </li>
                    <li>
                      <strong>Onboarding Discount:</strong> 15% off for the
                      first 12 months.
                      <ul className="ml-4 list-inside list-disc pb-2 pt-1 text-sm">
                        <li>
                          For the first 12 months, this brings the Lite plan to{' '}
                          <strong>$8.5/month</strong>{' '}
                          <span className="text-sm text-quaternary">
                            ($120 * (1 - 0.15) / 12)
                          </span>
                          {', '}
                          Pro plan to <strong>$20.5/month</strong>{' '}
                          <span className="text-sm text-quaternary">
                            ($290 * (1 - 0.15) / 12)
                          </span>
                        </li>
                      </ul>
                    </li>
                    <li>
                      <strong>Student Discount:</strong> 50% off Lite & Pro
                      plans. Email{' '}
                      <Button
                        onClick={() => {
                          router.push('mailto:support@quotion.co');
                        }}
                        variant="link"
                        size="sm"
                        className="h-auto px-0 py-0 text-base text-tertiary underline"
                      >
                        support@quotion.co
                      </Button>{' '}
                      from your .edu email address with your education details.
                    </li>
                    <li>
                      <FocusableLink
                        href="/partner"
                        target="_blank"
                        className="underline"
                      >
                        <strong>Partner Program:</strong>
                      </FocusableLink>{' '}
                      Earn commissions to offset subscription costs. Our
                      customers have already earned hundreds of dollars.
                    </li>
                  </ul>
                </FaqAccordionContent>
              </AccordionItem>
            </BlurFade>
            <BlurFade delay={0.25}>
              <AccordionItem value="raise-pricing">
                <FaqAccordionTrigger>
                  Will you increase the price?
                </FaqAccordionTrigger>
                <FaqAccordionContent>
                  Yes and no. We will increase the price after the next 50
                  subscription sales. However, we highly value our early
                  adopters, so we will not increase the price for existing
                  customers. If you join Quotion today, your plan price will
                  remain <strong>unchanged</strong> in the future.
                </FaqAccordionContent>
              </AccordionItem>
            </BlurFade>
            <BlurFade delay={0.3}>
              <AccordionItem value="change-plan">
                <FaqAccordionTrigger>
                  Can I change my plan later?
                </FaqAccordionTrigger>
                <FaqAccordionContent>
                  Of course. Our pricing scales with your business, you can
                  upgrade or downgrade your plan at any time.
                </FaqAccordionContent>
              </AccordionItem>
            </BlurFade>
            <BlurFade delay={0.35}>
              <AccordionItem value="item-3">
                <FaqAccordionTrigger>{`What's your refund policy?`}</FaqAccordionTrigger>
                <FaqAccordionContent>
                  {`We understand that things change. If you request a refund within 7 days of purchase, we'll issue a full refund.`}
                </FaqAccordionContent>
              </AccordionItem>
            </BlurFade>
            <BlurFade delay={0.4}>
              <AccordionItem value="payment-method">
                <FaqAccordionTrigger>
                  What payment methods do you support?
                </FaqAccordionTrigger>
                <FaqAccordionContent>
                  Our payment is provided by{' '}
                  <FocusableLink
                    className="underline"
                    href="https://stripe.com"
                    target="_blank"
                  >
                    Stripe
                  </FocusableLink>
                  . We accept all major credit and debit cards, PayPal, Google
                  Pay, and Apple Pay.
                </FaqAccordionContent>
              </AccordionItem>
            </BlurFade>
            <BlurFade delay={0.45}>
              <AccordionItem value="sites-pv">
                <FaqAccordionTrigger>
                  How do I know the pageview/visitor metrics for my sites?
                </FaqAccordionTrigger>
                <FaqAccordionContent>
                  Quotion has built-in web analytics, you can check it out on
                  the dashboard. No need for third-party solutions.
                </FaqAccordionContent>
              </AccordionItem>
            </BlurFade>
            <BlurFade delay={0.5}>
              <AccordionItem value="student-discount">
                <FaqAccordionTrigger>
                  Do you have a student discount?
                </FaqAccordionTrigger>
                <FaqAccordionContent>
                  Yes, we have a student discount (50% off) for Lite & Pro
                  plans. Please send an email from your .edu email address to
                  <Button
                    onClick={() => {
                      router.push('mailto:support@quotion.co');
                    }}
                    variant="link"
                    size="lg"
                    className="py-0"
                  >
                    Quotion support
                  </Button>
                  with your education details.
                </FaqAccordionContent>
              </AccordionItem>
            </BlurFade>
          </Accordion>
        </div>
        <div className="flex w-full flex-col items-center rounded-2xl bg-gray-50 px-8 pb-10 pt-8">
          <BlurFade delay={0.1} className="flex justify-center">
            <Avatar className="-me-4 size-12! rounded-full border-2 border-white">
              <Image width={48} height={48} alt="Michael" src={Avatar1} />
            </Avatar>
            <Avatar className="z-1 -mt-1.5 size-14! rounded-full border-2 border-white">
              <Image alt="Qing Wang" width={56} height={56} src={Qing} />
            </Avatar>
            <Avatar className="-ms-4 size-12! rounded-full border-2 border-white">
              <Image alt="Mei" src={Avatar3} width={48} height={48} />
            </Avatar>
          </BlurFade>
          <BlurFade delay={0.15}>
            <h3 className="pt-8 text-xl font-semibold text-gray-900">
              Still have questions?
            </h3>
          </BlurFade>
          <BlurFade delay={0.2}>
            <p className="pt-2 text-lg text-gray-600">
              Can't find the answer you're looking for? Please chat to our
              friendly team.
            </p>
          </BlurFade>
          <BlurFade delay={0.25} className="pt-8">
            <Button
              onClick={() => {
                router.push('mailto:support@quotion.co');
              }}
            >
              Get in Touch
            </Button>
          </BlurFade>
        </div>
      </section>
    </div>
  );
}

function FaqAccordionTrigger({
  className,
  ...props
}: React.ComponentProps<typeof AccordionTrigger>) {
  return (
    <AccordionTrigger
      {...props}
      className={cn('py-8 text-lg text-primary', className)}
    />
  );
}

function FaqAccordionContent(
  props: React.ComponentProps<typeof AccordionContent>,
) {
  return (
    <AccordionContent
      {...props}
      className={cn('text-start text-md text-tertiary', props.className)}
    />
  );
}
